import { useCallback, useMemo, useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { F0Button as Button, F0Select } from '@factorialco/f0-react'
import { EntityId, EntitySelectEntity } from '@factorialco/f0-react/dist/experimental'
import {
  WorkflowRoleSelectorDocument,
  WorkflowTeamSelectorDocument,
} from 'generated/resources/graphql'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import i18n from 'modules/core/lib/i18n'

import JobCatalogRoleSelector from './JobCatalogRoleSelector'
import LegacyRoleSelector from './LegacyRoleSelector'
import { ManualEmployeesSelector } from './ManualEmployeesSelector'
import { TeamsSelector } from './TeamsSelector'
import { StrategyItem, StrategyType } from './utils/interfaces'
import { getStrategyOptions } from './utils/strategyUtils'

interface ApprovalSettingsModalProps {
  entityLabel: string
  newEntityLabel: string
  placeholder: string
  isOpen: boolean
  onAddStrategies: (strategies: StrategyItem[]) => void
  onClose: () => void
  strategies?: StrategyItem[]
  unselectableEmployees?: Array<number>
  unselectableRoles?: Array<number | string>
  unselectableStrategies?: StrategyType[]
  unselectableTeams?: Array<number>
}

export const ApprovalSettingsModal = ({
  entityLabel,
  newEntityLabel,
  placeholder,
  isOpen,
  onAddStrategies,
  onClose,
  strategies = [],
  unselectableEmployees = [],
  unselectableRoles = [],
  unselectableStrategies = [],
  unselectableTeams = [],
}: ApprovalSettingsModalProps) => {
  const [strategy, setStrategy] = useState<StrategyType | undefined>(undefined)
  const [selectedEmployeesIds, setSelectedEmployeesIds] = useState<number[]>([])
  const [selectedTeamsIds, setSelectedTeamsIds] = useState<EntityId[]>([])
  const [selectedRoleId, setSelectedRoleId] = useState<string | undefined>(undefined)

  const strategyType = useMemo(
    () => (strategy ? getStrategyOptions(strategy).ui_type : 'none'),
    [strategy]
  )

  const { company } = useCurrentSession()

  const { loading: teamsLoading, data: teamsData } = useQuery(WorkflowTeamSelectorDocument, {
    skip: !isOpen || strategyType !== 'team',
  })

  const { data: rolesData, loading: rolesLoading } = useQuery(WorkflowRoleSelectorDocument, {
    variables: { companyId: company.id },
    skip: !isOpen || strategyType !== 'role',
  })

  const teams = teamsData?.teams?.teamsConnection?.nodes || undefined
  const legacyRoles = rolesData?.jobCatalog?.rolesConnection?.nodes || undefined
  const treeRoles = rolesData?.jobCatalog?.treeNodesConnection?.nodes || undefined

  const isLegacyJobCatalog =
    rolesData?.features?.featuresConnection?.nodes?.find(
      (feature) => feature.name === 'legacy_job_catalog'
    )?.enabled ?? true

  const resetModal = () => {
    setStrategy(undefined)
    setSelectedEmployeesIds([])
    setSelectedTeamsIds([])
    setSelectedRoleId(undefined)
  }

  const handleClose = () => {
    resetModal()
    onClose()
  }

  const handleSave = () => {
    if (!strategy) {
      return
    }

    const strategiesToAdd: StrategyItem[] = []

    if (strategyType === 'manual_selected_employees' && selectedEmployeesIds.length > 0) {
      selectedEmployeesIds.forEach((employeeId) => {
        if (typeof employeeId === 'number') {
          strategiesToAdd.push({
            id: `manual_selection_${employeeId}`,
            type: 'manual_selection',
            employeeId: employeeId,
          })
        }
      })
    } else if (strategyType === 'team' && selectedTeamsIds.length > 0) {
      const teamId = selectedTeamsIds[0]
      if (typeof teamId === 'number') {
        strategiesToAdd.push({
          id: `${strategy}_${teamId}_${Date.now()}`,
          type: strategy,
          teamId: teamId,
        })
      }
    } else if (strategyType === 'role' && selectedRoleId) {
      strategiesToAdd.push({
        id: `${strategy}_${selectedRoleId}_${Date.now()}`,
        type: strategy,
        roleId: selectedRoleId,
      })
    } else if (strategyType === 'none') {
      strategiesToAdd.push({
        id: `${strategy}_${Date.now()}`,
        type: strategy,
      })
    }

    if (strategiesToAdd.length > 0) {
      onAddStrategies(strategiesToAdd)
    }

    resetModal()
    onClose()
  }

  const availableTeams = useMemo(
    () => teams?.filter((team) => !unselectableTeams.includes(team.id)),
    [teams, unselectableTeams]
  )

  const availableLegacyRoles = useMemo(
    () => legacyRoles?.filter((role) => !unselectableRoles.includes(role.id)),
    [legacyRoles, unselectableRoles]
  )

  const availableTreeRoles = useMemo(
    () => treeRoles?.filter((role) => !unselectableRoles.includes(role.uuid)),
    [treeRoles, unselectableRoles]
  )

  const selectedTeamsEntities = useMemo(
    () =>
      availableTeams
        ?.filter((team) => selectedTeamsIds.includes(team.id))
        .map((team) => ({
          id: team.id,
          name: team.name,
        })),
    [availableTeams, selectedTeamsIds]
  )

  const isSaveDisabled = useMemo(() => {
    if (!strategyType) return true
    if (strategyType === 'none') return false
    if (strategyType === 'manual_selected_employees') return selectedEmployeesIds.length === 0
    if (strategyType === 'team') return selectedTeamsIds.length === 0
    if (strategyType === 'role') return !selectedRoleId
    return false
  }, [strategyType, selectedEmployeesIds, selectedTeamsIds, selectedRoleId])

  // All strategies, except none strategies already used
  const availableStrategies = useMemo(() => {
    return Object.values(getStrategyOptions())
      .filter((value) => {
        if (unselectableStrategies.includes(value.value)) return false
        if (value.ui_type !== 'none') return true
        return !strategies.some((s) => s.type === value.value)
      })
      .map((value) => value.value)
  }, [strategies, unselectableStrategies])

  const filteredStrategyOptions = useMemo(
    () =>
      Object.values(getStrategyOptions()).filter(({ ui_type, value }) => {
        // Filter out non available strategies
        if (!availableStrategies.map((s) => s.toString()).includes(value)) return false
        // Filter out role if no roles are available
        if (
          ui_type === 'role' &&
          availableLegacyRoles?.length === 0 &&
          availableTreeRoles?.length === 0
        )
          return false
        // Filter out team if no teams are available
        if (ui_type === 'team' && availableTeams?.length === 0) return false
        return true
      }),
    [
      availableStrategies,
      availableLegacyRoles?.length,
      availableTreeRoles?.length,
      availableTeams?.length,
    ]
  )

  const handleTeamSelect = useCallback(
    (selection: EntitySelectEntity[] | EntitySelectEntity | null) => {
      if (selection === null) {
        setSelectedTeamsIds([])
        return
      }
      const teamId = Array.isArray(selection) ? selection[0]?.id : selection.id
      setSelectedTeamsIds(teamId ? [teamId] : [])
    },
    []
  )

  if (!isOpen) return null

  return (
    <Modal
      disableFocusTrap
      title={newEntityLabel}
      footer={
        <>
          <Button
            label={i18n.t('common.cancel')}
            variant="outline"
            size="md"
            onClick={handleClose}
          />
          <Button
            label={i18n.t('common.save_changes')}
            variant="default"
            size="md"
            onClick={handleSave}
            disabled={isSaveDisabled}
          />
        </>
      }
      onClose={handleClose}
    >
      <Box gap="s16">
        <Box gap="s8" width="full">
          <Text size="200" weight="medium" className="text-f1-foreground-secondary">
            {entityLabel}
          </Text>

          <Box gap="s8" width="full">
            <F0Select
              size="md"
              label="Strategy selector"
              hideLabel
              value={strategy}
              onChange={setStrategy}
              options={filteredStrategyOptions}
              placeholder={placeholder}
            />

            {strategyType === 'manual_selected_employees' && (
              <ManualEmployeesSelector
                value={selectedEmployeesIds}
                onChange={setSelectedEmployeesIds}
                excludeIds={unselectableEmployees}
              />
            )}

            {strategyType === 'team' && (
              <TeamsSelector
                availableTeams={availableTeams}
                loading={teamsLoading}
                value={selectedTeamsEntities}
                onChange={handleTeamSelect}
              />
            )}

            {strategyType === 'role' &&
              (isLegacyJobCatalog ? (
                <LegacyRoleSelector
                  availableRoles={availableLegacyRoles}
                  isLoading={rolesLoading}
                  onChange={(id) => setSelectedRoleId(id ?? undefined)}
                  value={selectedRoleId}
                />
              ) : (
                <JobCatalogRoleSelector
                  isLoading={rolesLoading}
                  onChange={(id) => setSelectedRoleId(id ?? undefined)}
                  treeRoles={availableTreeRoles}
                  value={selectedRoleId}
                />
              ))}
          </Box>
        </Box>
      </Box>
    </Modal>
  )
}
